'use client';

import { useState } from 'react';
import DataFilterBar from './DataFilterBar';
import PopulationChart from './PopulationChart';
import RegionalDataMap from './RegionalDataMap';
import DataExportPanel from './DataExportPanel';

interface FilterState {
  region: string;
  timePeriod: string;
  threatType: string;
  searchQuery: string;
}

interface PopulationDataPoint {
  year: string;
  whiteRhino: number | null;
  blackRhino: number | null;
  total: number;
}

interface ThreatData {
  id: string;
  name: string;
  severity: 'critical' | 'high' | 'moderate' | 'low';
  incidents: number;
  trend: 'increasing' | 'stable' | 'decreasing';
  description: string;
  affectedRegions: string[];
}

interface RegionData {
  id: string;
  name: string;
  country: string;
  population: number;
  area: string;
  coordinates: { lat: number; lng: number };
  status: string;
  lastUpdated: string;
}

const ConservationDataInteractive = () => {
  const [filters, setFilters] = useState<FilterState>({
    region: 'all',
    timePeriod: '2025',
    threatType: 'all',
    searchQuery: ''
  });

  const populationData: PopulationDataPoint[] = [
    { year: '2020', whiteRhino: 18000, blackRhino: 5630, total: 23630 },
    { year: '2021', whiteRhino: 17212, blackRhino: 5366, total: 22578 },
    { year: '2022', whiteRhino: 15942, blackRhino: 6195, total: 22137 },
    { year: '2023', whiteRhino: 15942, blackRhino: 6195, total: 22137 },
    { year: '2024', whiteRhino: 17464, blackRhino: 6421, total: 23885 },
    { year: '2025', whiteRhino: 15752, blackRhino: 6788, total: 22540 }
  ];

  const threatData: ThreatData[] = [
    {
      id: 'poaching',
      name: 'Poaching & Illegal Wildlife Trade',
      severity: 'critical',
      incidents: 394,
      trend: 'decreasing',
      description: 'Despite ongoing efforts, poaching remains the primary threat to rhino populations. Organized criminal networks continue to target rhinos for their horns, driven by demand in illegal markets.',
      affectedRegions: ['Kruger National Park', 'Hluhluwe-iMfolozi', 'Etosha National Park']
    },
    {
      id: 'habitat-loss',
      name: 'Habitat Loss & Fragmentation',
      severity: 'high',
      incidents: 127,
      trend: 'increasing',
      description: 'Expanding human settlements and agricultural activities are reducing and fragmenting critical rhino habitats, limiting movement corridors and genetic diversity.',
      affectedRegions: ['Lewa Wildlife Conservancy', 'Ol Pejeta Conservancy', 'Kruger National Park']
    },
    {
      id: 'human-conflict',
      name: 'Human-Wildlife Conflict',
      severity: 'moderate',
      incidents: 89,
      trend: 'stable',
      description: 'As human populations expand into wildlife areas, conflicts arise over land use, water resources, and crop damage, threatening both communities and rhino populations.',
      affectedRegions: ['Etosha National Park', 'Hluhluwe-iMfolozi', 'Lewa Wildlife Conservancy']
    },
    {
      id: 'disease',
      name: 'Disease Outbreaks',
      severity: 'moderate',
      incidents: 43,
      trend: 'stable',
      description: 'Infectious diseases, including tuberculosis and anthrax, pose ongoing risks to rhino populations, particularly in areas with high wildlife density.',
      affectedRegions: ['Kruger National Park', 'Hluhluwe-iMfolozi']
    },
    {
      id: 'climate',
      name: 'Climate Change Impact',
      severity: 'high',
      incidents: 156,
      trend: 'increasing',
      description: 'Changing rainfall patterns, prolonged droughts, and extreme weather events are affecting water availability and vegetation, impacting rhino habitats and food sources.',
      affectedRegions: ['All Regions']
    }
  ];

  const regionalData: RegionData[] = [
    {
      id: 'kruger',
      name: 'Kruger National Park',
      country: 'South Africa',
      population: 2750,
      area: '19,485 km²',
      coordinates: { lat: -24.0094, lng: 31.4987 },
      status: 'Recovering But Historically Declined',
      lastUpdated: '15 Jan 2026'
    },
    {
      id: 'hluhluwe',
      name: 'Hluhluwe-iMfolozi Park',
      country: 'South Africa',
      population: 1500,
      area: '960 km²',
      coordinates: { lat: -28.2667, lng: 32.0833 },
      status: 'Stable/Recovering',
      lastUpdated: '12 Jan 2026'
    },
    {
      id: 'etosha',
      name: 'Etosha National Park',
      country: 'Namibia',
      population: 1200,
      area: '22,270 km²',
      coordinates: { lat: -18.8556, lng: 16.3293 },
      status: 'Improving',
      lastUpdated: '10 Jan 2026'
    },
    {
      id: 'lewa',
      name: 'Lewa Wildlife Conservancy',
      country: 'Kenya',
      population: 189,
      area: '250 km²',
      coordinates: { lat: 0.2167, lng: 37.4167 },
      status: 'Improving',
      lastUpdated: '18 Jan 2026'
    },
    {
      id: 'ol-pejeta',
      name: 'Ol Pejeta Conservancy',
      country: 'Kenya',
      population: 156,
      area: '360 km²',
      coordinates: { lat: 0.0167, lng: 36.9167 },
      status: 'Improving',
      lastUpdated: '16 Jan 2026'
    },
    {
      id: 'matobo',
      name: 'Matobo National Park',
      country: 'Zimbabwe',
      population: 125,
      area: '3,100 km²',
      coordinates: { lat: -20.5500, lng: 28.5000 },
      status: 'Stable(Fragile)',
      lastUpdated: '20 Jan 2026'
    }
  ];

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  return (
    <div className="space-y-6">
      <DataFilterBar onFilterChange={handleFilterChange} />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <PopulationChart data={populationData} />
        </div>
        <div>
          <DataExportPanel />
        </div>
      </div>

      <RegionalDataMap regions={regionalData} />
    </div>
  );
};

export default ConservationDataInteractive;